import { useQuery } from "@tanstack/react-query";
import { Clock, MessageSquare } from "lucide-react";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { date, money, num } from "../lib/format";
import { offerChip } from "../pages/Designs";
import { LeadActivity } from "./LeadActivity";
import { ProposalActions } from "./ProposalActions";
import { useOrg } from "../api/org";
import { LeadLanguageRow } from "../i18n/HomeownerLanguage";
import { Alert, Badge, Card, Spinner } from "./ui";
import { useT, useIntlShape } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/keys";
import type { Tr } from "../lib/tr";

type Summary = S["QuotePriceSummary"];

const FINANCING: Record<string, MessageKey> = {
  cash: "designs.financing.cash",
  loan: "designs.financing.loan",
  lease: "designs.financing.lease",
  ppa: "designs.financing.ppa",
};

/** What changed from one revision to the next ("Panels 18 → 16"). */
function diff(before: Summary, after: Summary, t: Tr): string[] {
  const out: string[] = [];
  const c = after.currency;
  const financing = (f: string | null | undefined) => (f && FINANCING[f] ? t(FINANCING[f]!) : f ?? "—");
  const battery = (b: string | null | undefined) => b ?? t("designs.offerCard.noBattery");
  if (before.panel_count !== after.panel_count) out.push(t("designs.offerCard.diffPanels", { before: num(before.panel_count), after: num(after.panel_count) }));
  if (before.system_kw !== after.system_kw) out.push(t("designs.offerCard.diffKw", { before: num(before.system_kw, 1), after: num(after.system_kw, 1) }));
  if ((before.battery_label ?? null) !== (after.battery_label ?? null))
    out.push(t("designs.offerCard.diffBattery", { before: battery(before.battery_label), after: battery(after.battery_label) }));
  if ((before.financing ?? null) !== (after.financing ?? null))
    out.push(t("designs.offerCard.diffChange", { before: financing(before.financing), after: financing(after.financing) }));
  if (before.net_cost !== after.net_cost) out.push(t("designs.offerCard.diffNet", { before: money(before.net_cost, c), after: money(after.net_cost, c) }));
  return out;
}

/** The official quote of a website lead: number, status, validity, the homeowner's notes, revisions, what happened
 * to its emails and link (LeadActivity) and the proposal actions (Share, Resend, Download proposal). */
export function QuoteOfferCard({ orgId, publicQuoteId, homeownerName }: { orgId: string; publicQuoteId: string; homeownerName?: string | null }) {
  const t = useT();
  const intl = useIntlShape();
  const canEdit = useOrg().can("designs_leads", "edit");
  const offer = useQuery({
    queryKey: ["lead-offer", orgId, publicQuoteId],
    queryFn: () =>
      unwrap(api.GET("/v1/orgs/{org_id}/public-quotes/{quote_id}/offer", { params: { path: { org_id: orgId, quote_id: publicQuoteId } } })),
  });

  if (offer.isLoading) return <Card title={t("designs.offerCard.official")}><Spinner /></Card>;
  if (offer.error || !offer.data) return <Card title={t("designs.offerCard.official")}><Alert tone="danger">{errorMessage(offer.error)}</Alert></Card>;
  const o = offer.data.offer;
  const revisions = offer.data.revisions;
  const chip = offerChip(o.status);

  return (
    <Card
      title={o.quote_number ? t("designs.offerCard.quoteNumber", { number: o.quote_number }) : t("designs.detail.proposal")}
      description={
        o.quote_number
          ? o.committed_at
            ? t("designs.offerCard.validConfirmed", { valid: date(o.valid_until), confirmed: date(o.committed_at, true) })
            : t("designs.offerCard.valid", { valid: date(o.valid_until) })
          : t("designs.offerCard.notConfirmed")
      }
      actions={chip ? <Badge tone={chip.tone}>{t(chip.label)}</Badge> : <Badge>{t("designs.offer.draft")}</Badge>}
    >
      <div className="stack" style={{ gap: 14 }}>
        <LeadLanguageRow orgId={orgId} publicQuoteId={publicQuoteId} offer={o} canEdit={canEdit} />
        {(o.best_time || o.notes) && (
          <div className="stack" style={{ gap: 6 }}>
            {o.best_time && (
              <div className="row small">
                <Clock size={14} className="faint" /> {intl.formatMessage({ id: "designs.offerCard.bestTime" }, { time: o.best_time, b: (c) => <b>{c}</b> })}
              </div>
            )}
            {o.notes && (
              <div className="row small" style={{ alignItems: "flex-start", flexWrap: "nowrap" }}>
                <MessageSquare size={14} className="faint" style={{ marginTop: 3, flex: "none" }} /> <span style={{ whiteSpace: "pre-line" }}>{o.notes}</span>
              </div>
            )}
          </div>
        )}
        {revisions.length > 0 && (
          <ol className="timeline">
            {revisions.map((r, i) => {
              const changes = i > 0 ? diff(revisions[i - 1]!.price_summary, r.price_summary, t) : [];
              const p = r.price_summary;
              return (
                <li key={r.number}>
                  <div className="timeline-head">
                    <b>{i === 0 ? t(o.action === "booking_opened" ? "designs.offerCard.confirmedBooking" : "designs.offerCard.confirmedInterested") : t("designs.offerCard.revision", { number: r.number })}</b>
                    <span className="small muted">
                      {date(r.created_at, true)} · {r.author}
                    </span>
                  </div>
                  <div className="small muted">
                    {i === 0
                      ? [t("designs.list.panels", { count: p.panel_count }), `${num(p.system_kw, 1)} kW`, p.battery_label, money(p.net_cost, p.currency)].filter(Boolean).join(" · ")
                      : changes.length
                        ? changes.join(" · ")
                        : t("designs.offerCard.repriced")}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <LeadActivity orgId={orgId} publicQuoteId={publicQuoteId} />
        <ProposalActions
          orgId={orgId}
          publicQuoteId={publicQuoteId}
          linkLast4={o.link_last4}
          homeownerName={[o.contact?.first_name, o.contact?.last_name].filter(Boolean).join(" ") || homeownerName}
          address={offer.data.address}
          quoteNumber={o.quote_number}
          locale={o.locale}
        />
      </div>
    </Card>
  );
}
